import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { FiArrowLeft, FiDownload } from "react-icons/fi"

import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"
import { type ExportColumn, exportToExcel } from "@/utils/exportToExcel"

export const Route = createFileRoute("/_layout/students/$studentId/account")({
  component: StudentAccountPage,
})

interface AccountEnrollmentItem {
  enrollment_id: string
  class_name: string | null
  session_name: string | null
  status: string | null
  enrollment_date: string | null
  amount_due: number
  amount_paid: number
  balance: number
  payment_status: string | null
}

interface AccountSummary {
  total_due: number
  total_paid: number
  outstanding_balance: number
}

interface StudentAccountStudent {
  first_name: string
  last_name: string
  id: string
}

interface StudentAccountData {
  student: StudentAccountStudent
  enrollments: AccountEnrollmentItem[]
  summary: AccountSummary
}

function StudentAccountPage() {
  const { studentId } = Route.useParams()
  const { hasPermission } = usePermissions()

  const { data, isLoading } = useQuery({
    queryKey: ["student-account", studentId],
    queryFn: async () => {
      const response = await api.instance.get(
        `/api/v1/students/${studentId}/account`,
      )
      return unwrapStandardResponse(response.data) as StudentAccountData
    },
    enabled: !!studentId,
  })

  const formatCurrency = (amount: number) => `$${Number(amount).toFixed(2)}`

  const getStatusBadge = (status: string | null) => {
    const colors: Record<string, string> = {
      paid: "green",
      partial: "orange",
      pending: "red",
      refunded: "gray",
    }
    return (
      <Badge colorPalette={colors[status || ""] || "gray"}>
        {(status || "N/A").toUpperCase()}
      </Badge>
    )
  }

  const accountExportColumns: ExportColumn<AccountEnrollmentItem>[] = [
    { header: "Session", accessor: (e) => e.session_name },
    { header: "Class", accessor: (e) => e.class_name },
    { header: "Status", accessor: (e) => e.status },
    {
      header: "Enrollment Date",
      accessor: (e) =>
        e.enrollment_date
          ? new Date(e.enrollment_date).toLocaleDateString()
          : "",
    },
    { header: "Amount Due", accessor: (e) => e.amount_due.toFixed(2) },
    { header: "Amount Paid", accessor: (e) => e.amount_paid.toFixed(2) },
    { header: "Balance", accessor: (e) => e.balance.toFixed(2) },
    { header: "Payment Status", accessor: (e) => e.payment_status },
  ]

  const handleExport = () => {
    if (!data) return
    const studentName = `${data.student.first_name}-${data.student.last_name}`
    const today = new Date().toISOString().slice(0, 10)
    exportToExcel(
      data.enrollments,
      accountExportColumns,
      `account-${studentName}-${today}`,
      "Account",
    )
  }

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex align="center" justify="center" minH="240px" direction="column" gap={4}>
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading account...</Text>
        </Flex>
      </Container>
    )
  }

  if (!data) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text>Account not found.</Text>
      </Container>
    )
  }

  const { student, enrollments, summary } = data

  return (
    <Container maxW="container.xl" py={{ base: 4, md: 8 }}>
      {/* Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        mb={6}
        gap={3}
      >
        <HStack gap={3}>
          <Link to="/students" search={{ page: 1, sort: "created_at", direction: "desc" }}>
            <Button variant="ghost" size="sm">
              <FiArrowLeft />
            </Button>
          </Link>
          <Box>
            <Heading size="lg">
              {student.first_name} {student.last_name}
            </Heading>
            <Text color="fg.muted" fontSize="sm">
              Account & Balance Sheet
            </Text>
          </Box>
        </HStack>
        {hasPermission("students:read") && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            disabled={enrollments.length === 0}
          >
            <HStack gap={1}>
              <FiDownload />
              <Text>Export Account</Text>
            </HStack>
          </Button>
        )}
      </Flex>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={4} mb={6}>
        <Box
          p={4}
          borderWidth="1px"
          borderRadius="lg"
          bg="white"
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            Total Due
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {formatCurrency(summary.total_due)}
          </Text>
        </Box>
        <Box
          p={4}
          borderWidth="1px"
          borderRadius="lg"
          bg="white"
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            Total Paid
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color="green.600">
            {formatCurrency(summary.total_paid)}
          </Text>
        </Box>
        <Box
          p={4}
          borderWidth="1px"
          borderRadius="lg"
          bg="white"
        >
          <Text fontSize="sm" color="fg.muted" mb={1}>
            Outstanding Balance
          </Text>
          <Text
            fontSize="2xl"
            fontWeight="bold"
            color={summary.outstanding_balance > 0 ? "red.600" : "green.600"}
          >
            {formatCurrency(summary.outstanding_balance)}
          </Text>
        </Box>
      </SimpleGrid>

      {/* Enrollment History Table */}
      <Box>
        <Heading size="md" mb={4}>
          Enrollment History
        </Heading>
        {enrollments.length === 0 ? (
          <Box
            borderWidth="1px"
            borderRadius="lg"
            p={8}
            textAlign="center"
          >
            <Text color="fg.muted">No enrollments found for this student.</Text>
          </Box>
        ) : (
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table.Root variant="outline" size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Session</Table.ColumnHeader>
                  <Table.ColumnHeader>Class</Table.ColumnHeader>
                  <Table.ColumnHeader>Enrolled</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Amount Due
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Paid
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    Balance
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>Status</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {enrollments.map((enrollment) => (
                  <Table.Row key={enrollment.enrollment_id}>
                    <Table.Cell>
                      <Text>{enrollment.session_name || "N/A"}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontWeight="medium">
                        {enrollment.class_name || "N/A"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm">
                        {enrollment.enrollment_date
                          ? new Date(
                              enrollment.enrollment_date,
                            ).toLocaleDateString()
                          : "N/A"}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text fontWeight="semibold">
                        {formatCurrency(enrollment.amount_due)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text color="green.600">
                        {formatCurrency(enrollment.amount_paid)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text
                        fontWeight="bold"
                        color={
                          enrollment.balance > 0 ? "red.600" : "green.600"
                        }
                      >
                        {formatCurrency(enrollment.balance)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      {getStatusBadge(enrollment.payment_status)}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Box>
    </Container>
  )
}
